import classNames from 'classnames'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { FormEventHandler, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'
import { useNavigate } from '@lib/react-router-dom'

import { BaseModalType, useModal } from '@context/ModalsContext'

import { ExchangeRateBox } from '@ui/ExchangeRateBox/ExchangeRateBox'
import { Button } from '@ui/components/Button'
import { Modal } from '@ui/components/Modal'
import { PopoverInput } from '@ui/components/Popover'
import { CaretIcon } from '@ui/icons/Caret.icon'

import { FormSubmitValues } from '@utils/formValidation'
import { CommonPaths } from '@utils/path'
import { WalletRestrictions, isRestricted, isWalletSuspended } from '@utils/wallet.utils'

import { NameDto } from '@model/NameDto'
import { TradingAccount } from '@model/TradingAccount'
import { TransactionLimitDto } from '@model/TransactionLimitDto'
import { CurrencyType, WalletCurrency, WalletDto, WalletTypeEnum } from '@model/WalletDto'

import { FieldGroup } from '@global/form/FieldGroup'
import { createFormField } from '@global/formField/FormField'
import { createFormNumericField } from '@global/formField/FormNumericField'
import { FormTemplate } from '@global/templates/FormTemplate'
import { TransactionFormActionButtonTemplate } from '@global/templates/TransactionFormActionButtonTemplate'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { FieldWrapper } from '@shared/components/FieldWrapper'
import { WalletRestriction } from '@shared/components/WalletRestriction/WalletRestriction'
import { ActionModal } from '@shared/modals/Action.modal'

import { ExchangeRateModal } from '../../modals/ExchangeRateModal.modal'

import styles from './ConversionForm.module.scss'

export enum ConversionDirection {
  From = 'from',
  To = 'to',
}

interface CreateConversionFormValues {
  from: SelectableOptionField | null
  to: SelectableOptionField | null
  amountFrom: number | null
  amountTo: number | null
  minAmount: number
  direction: ConversionDirection
}

export enum ConversionType {
  Wallet = 'wallet',
  TradingAccount = 'tradingAccount',
}

export interface SelectableOptionField {
  id: string
  name: string
  currency: CurrencyType
  type: ConversionType
  balance: number
  displayType: string
  restrictions?: NameDto[]
  walletTypeId?: WalletTypeEnum
}

export interface CreateConversionFormSubmitValues {
  fromId: string
  toId: string
  fromCurrencyId?: CurrencyType
  toCurrencyId?: CurrencyType
  fromAmount: number
  toAmount: number
  direction: ConversionDirection
  fromType: SelectableOptionField['type']
  toType: SelectableOptionField['type']
}

export interface FetchRateOptions {
  BaseWalletTypeId?: WalletTypeEnum
  QuoteWalletTypeId?: WalletTypeEnum
}

const FormField = createFormField<CreateConversionFormValues>()
const FormNumericField = createFormNumericField<CreateConversionFormValues>()

const ConversionFormUI: React.FC<FormikProps<CreateConversionFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, values, setFieldValue, isValid, limits } = props

  const { t } = useTranslation()
  const cancelModal = useModal(ActionModal, {
    onConfirm: () => navigate(CommonPaths.TradersRoomBalances),
  })
  const navigate = useNavigate()
  const { formatNumber, formatMoney } = useFormatNumber()

  const exchangeRateModal = useModal(ExchangeRateModal)

  const { calculateAmount, fromOptions, toOptions } = useConversionOptions(props, values)

  const fromModal = useModal(SelectFromModal, {
    limits: limits,
    options: fromOptions,
    setFieldValue,
    values,
  })

  useFetchRate(props, values)

  const handleSubmitForm: FormEventHandler = (event) => {
    event.preventDefault()
    handleSubmit()
  }

  useEffect(() => {
    if (!toOptions?.length || !values.to) {
      return
    }
    if (!toOptions.some((option) => option.id === values.to?.id)) {
      setFieldValue('to', null)
    }
    if (values.from?.type !== values.to?.type) {
      setFieldValue('to', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toOptions, values.from, values.to])

  useEffect(() => {
    if (values.direction === ConversionDirection.From) {
      setFieldValue('amountTo', calculateAmount(values.amountFrom).toFixed(2))
    } else {
      setFieldValue('amountFrom', calculateAmount(values.amountTo, true).toFixed(2))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.amountFrom, values.amountTo, props.exchangeRate, values.direction])

  useEffect(() => {
    if ((values.amountTo && values.amountTo > 0) || (values.amountFrom && values.amountFrom > 0)) {
      props.setFieldTouched('amountFrom', true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.amountFrom, values.amountTo])

  const getToOptions = () => {
    return toOptions
      ?.filter((option) => filterCurrentSelected(option, values.from))
      ?.filter((option) => filterByFromType(option, values.from))
      .map((option) => ({
        id: option.id,
        name: `${option.currency} ${option.displayType}`,
        disabled: !isWalletTransferToAllowed(option),
      }))
  }

  return (
    <FormTemplate title={t('Transactions.FX Conversion')} goBack={cancelModal.open}>
      <Form onSubmit={handleSubmitForm}>
        <p className={classNames(styles.info)}>
          <span className='danger'>{t('Trading Account.Important')}</span>{' '}
          {t(
            'Transactions.Currency conversions can only take place between Wallets or between Trading Accounts'
          )}
        </p>
        <FieldWrapper>
          <FormField
            name='from.name'
            label={t('Transactions.Convert from')}
            rightIcon={CaretIcon.Down}
            value={values.from ? `${values.from?.currency} ${values.from?.displayType}` : ''}
            readOnly
            hint={
              values.from &&
              `${t('Trading Account.Balance')} ${formatMoney(
                values.from.balance,
                values.from.currency
              )}`
            }
            required
            onClick={() => fromModal.open()}
          />
          <FieldGroup>
            <FormNumericField
              name={'amountFrom'}
              onChange={(v) => {
                const numberValue = Number(v.target.value)
                setFieldValue('direction', ConversionDirection.From)
                setFieldValue('amountFrom', numberValue)
              }}
              decimalPlaces={2}
              label={t('Transactions.Amount sent')}
              required
              step={values.minAmount}
              disabled={!props.exchangeRate || props.rateLoading}
            />
            <div className={styles.currencyFieldWrapper}>
              <FormField
                name={'from.currency'}
                label={t('Wallet.Currency')}
                value={values.from?.currency}
                disabled
              />
            </div>
          </FieldGroup>
          <PopoverInput
            label={
              values.to
                ? `${values.to?.currency} ${values.to?.displayType}`
                : t('Transactions.Convert to')
            }
            value={values.to ? values.to.id : ''}
            hint={
              values.to &&
              `${t('Trading Account.Balance')} ${formatMoney(
                values.to.balance,
                values.to.currency
              )}`
            }
            disabled={!values?.from}
            readOnly
            required
            options={getToOptions()}
            onChange={(options) => {
              const selectedOption = toOptions?.find((option) => option.id === options.id)
              if (selectedOption) {
                setFieldValue('to', selectedOption)
              }
            }}
          />
          {values.to && values.from && (
            <FieldGroup>
              <FormNumericField
                name={'amountTo'}
                decimalPlaces={2}
                onChange={(v) => {
                  const numberValue = Number(v.target.value)
                  setFieldValue('direction', ConversionDirection.To)
                  setFieldValue('amountTo', numberValue)
                }}
                label={t('Transactions.Amount received')}
                required
                disabled={!props.exchangeRate || props.rateLoading}
              />
              <div className={styles.currencyFieldWrapper}>
                <FormField
                  name={'to.currency'}
                  label={t('Wallet.Currency')}
                  value={values.to?.currency}
                  disabled
                />
              </div>
            </FieldGroup>
          )}
          {values.from && values.to && (
            <ExchangeRateBox
              infoIconClick={() => exchangeRateModal.open()}
              currencyFrom={values.from.currency}
              currencyTo={values.to.currency}
              calculatedAmount={formatNumber(calculateAmount(1, false), { maxDecimalPlaces: 6 })}
            />
          )}
          <TransactionFormActionButtonTemplate>
            <Button.Secondary size='lg' type='button' onClick={() => cancelModal.open()}>
              {t('Cancel')}
            </Button.Secondary>

            <Button.Primary size='lg' type='submit' disabled={!isValid}>
              {t('Confirm')}
            </Button.Primary>
          </TransactionFormActionButtonTemplate>
        </FieldWrapper>
      </Form>
    </FormTemplate>
  )
}

interface SelectFromModalProps {
  limits: TransactionLimitDto[]
  options: SelectableOptionField[]
  values: CreateConversionFormValues
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<CreateConversionFormValues>>
}

const getSelectableOptionValue = (option: SelectableOptionField) => {
  return `${option.type}:${option.id}:${option.currency}`
}

const SelectFromModal: BaseModalType<SelectFromModalProps> = (props) => {
  const { options, close, limits, values, setFieldValue } = props
  const { formatMoney } = useFormatNumber()

  const sortOptions = (a: SelectableOptionField, b: SelectableOptionField) => {
    return a.currency.localeCompare(b.currency)
  }

  const filterIfBalanceNegative = (option: SelectableOptionField) => {
    return option?.balance >= 0
  }

  const fromOptions = options.sort(sortOptions).filter(filterIfBalanceNegative)

  const { t } = useTranslation()

  const handleOption = (option?: SelectableOptionField) => {
    if (!option) return

    setFieldValue('from', option)
    if (option.type === ConversionType.TradingAccount) setFieldValue('minAmount', 0.01)

    if (option.type === ConversionType.Wallet) {
      const isMinAmount = limits.find(
        (x) => x.walletType?.id === WalletTypeEnum.ETD && x.currency.id === option.currency
      )
      if (isMinAmount) setFieldValue('minAmount', isMinAmount.amount)
      else setFieldValue('minAmount', 0.01)
    }
    if (option.currency === values.to?.currency) setFieldValue('to', null)

    close()
  }

  const isWalletTransferFromAllowed = (wallet: SelectableOptionField) =>
    !(
      isRestricted(WalletRestrictions.TRANSFER_FROM, wallet.restrictions) ||
      isWalletSuspended(wallet?.restrictions ?? [])
    )

  function groupByCurrency<T extends { currency?: string }>(arr: T[]) {
    return arr.reduce<Record<string, T[]>>((acc, item) => {
      const key = item.currency || 'UNKNOWN'
      ;(acc[key] ??= []).push(item)
      return acc
    }, {})
  }

  const byCurrency = groupByCurrency<SelectableOptionField>(fromOptions)

  return (
    <Modal>
      <Modal.Header>{t('Trading Account.Convert From')}</Modal.Header>
      <Modal.Body>
        {Object.keys(byCurrency).map((currency, idx) => (
          <React.Fragment key={`section-${idx}`}>
            {byCurrency[currency] && <Modal.Body.Title>{currency}</Modal.Body.Title>}
            {byCurrency[currency].length > 0 && (
              <Modal.Body.Input.Radio
                name='period-modal'
                options={byCurrency[currency].map((x) => ({
                  label: `${x.currency} ${x.displayType}`,
                  value: getSelectableOptionValue(x),
                  disabled: !isWalletTransferFromAllowed(x),
                  caption: (
                    <>
                      {`${t('Trading Account.Balance')} ${formatMoney(x.balance, x.currency)}`}
                      {!isWalletTransferFromAllowed(x) ? (
                        <WalletRestriction
                          restrictions={x.restrictions}
                          restrictionType={WalletRestrictions.TRANSFER_FROM}
                          onClose={close}
                        />
                      ) : null}
                    </>
                  ),
                }))}
                onChange={(x: string) =>
                  handleOption(byCurrency[currency].find((y) => getSelectableOptionValue(y) === x))
                }
              />
            )}
          </React.Fragment>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}

const isWalletTransferToAllowed = (wallet: SelectableOptionField) =>
  !(
    isRestricted(WalletRestrictions.TRANSFER_TO, wallet.restrictions) ||
    isWalletSuspended(wallet?.restrictions ?? [])
  )

const filterCurrentSelected = (
  option: SelectableOptionField,
  from: SelectableOptionField | null
) => {
  return from?.currency !== option?.currency
}

const filterByFromType = (option: SelectableOptionField, from: SelectableOptionField | null) => {
  return option.type === from?.type
}

const useConversionOptions = (props: OuterProps, values: CreateConversionFormValues) => {
  const { t } = useTranslation()
  const getWallets = useMemo(() => {
    return props.wallets.map<SelectableOptionField>((x) => ({
      id: x.id,
      name: x.name,
      currency: x.currency.id,
      balance: x.balance,
      displayType: t('Wallet.Wallet'),
      type: ConversionType.Wallet,
      restrictions: x.restrictions,
      walletTypeId: x.walletType.id,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wallets])

  const getTradingAccounts = useMemo(() => {
    const [tradingAccount] = props.tradingAccounts
    const balances = tradingAccount?.platformOverview?.balances || []
    return balances?.map<SelectableOptionField>((x) => ({
      id: tradingAccount.id,
      name: tradingAccount.name,
      currency: x.currency,
      balance: x.balance,
      displayType: t('Trading Account.Trading Account'),
      type: ConversionType.TradingAccount,
      walletTypeId:
        props.wallets.find((wallet) => wallet.id === tradingAccount?.wallet?.id)?.walletType.id ??
        WalletTypeEnum.ETD,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tradingAccounts, props.wallets])

  const fromOptions = useMemo(() => {
    if (!values.from) {
      return [...getWallets, ...getTradingAccounts]
    }
    if (values.from.type === ConversionType.Wallet) {
      return getWallets
    } else if (values.from.type === ConversionType.TradingAccount) {
      return getTradingAccounts
    }
    return []
  }, [values.from, getWallets, getTradingAccounts])

  const calculateAmountByExchangeRate = (amount: number, exchangeRate: number) => {
    return amount * exchangeRate
  }

  const calculateAmountByReverseExchangeRate = (amount: number, exchangeRate: number) => {
    return amount / exchangeRate
  }

  const toOptions = useMemo(() => {
    if (values.from?.type === ConversionType.Wallet) {
      return fromOptions
    }
    if (values.from?.type === ConversionType.TradingAccount) {
      return props.availableTACurrencies.map(
        (x) =>
          getTradingAccounts.find((tradingAccount) => tradingAccount.currency === x.id) || {
            id: x.id,
            currency: x.id,
            displayType: t('Trading Account.Trading Account'),
            type: ConversionType.TradingAccount,
            name: '',
            balance: 0,
            walletTypeId: WalletTypeEnum.ETD,
          }
      )
    }
  }, [values.from?.type, fromOptions, props.availableTACurrencies, getTradingAccounts, t])

  const calculateAmount = useCallback(
    (amount: number | null, reverse?: boolean) =>
      amount
        ? reverse
          ? calculateAmountByReverseExchangeRate(amount, props.exchangeRate)
          : calculateAmountByExchangeRate(amount, props.exchangeRate)
        : 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.exchangeRate]
  )

  return {
    fromOptions,
    toOptions,
    calculateAmount,
  }
}

const useFetchRate = (props: OuterProps, values: CreateConversionFormValues) => {
  const { fetchRate } = props

  useEffect(() => {
    let delayDebounceFn: NodeJS.Timeout
    if (values.from?.currency && values.to?.currency) {
      delayDebounceFn = setTimeout(() => {
        const from = values.from
        const to = values.to
        if (!from || !to) return

        const isEtdConversion =
          from.walletTypeId === WalletTypeEnum.ETD && to.walletTypeId === WalletTypeEnum.ETD

        const rateOptions: FetchRateOptions | undefined = isEtdConversion
          ? {
              BaseWalletTypeId: from.walletTypeId,
              QuoteWalletTypeId: to.walletTypeId,
            }
          : undefined

        fetchRate(from.currency as CurrencyType, to.currency as CurrencyType, rateOptions)
      }, 1000)
    }
    return () => clearTimeout(delayDebounceFn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.from, values.to])
}

function dataToSubmit(
  values: CreateConversionFormValues
): FormSubmitValues<CreateConversionFormSubmitValues> {
  return {
    fromType: values.from?.type as ConversionType,
    toType: values.to?.type as ConversionType,
    fromId: values.from?.id || '',
    toId: values.to?.id || '',
    direction: values.direction || ConversionDirection.From,
    fromCurrencyId: values?.from?.currency,
    toCurrencyId: values?.to?.currency,
    fromAmount: Number.parseFloat((values.amountFrom || 0).toString()),
    toAmount: Number.parseFloat((values.amountTo || 0).toString()),
  }
}

export interface PresetAccount {
  account?: TradingAccount
  currency?: CurrencyType
  wallet?: WalletDto
  type: ConversionType
  displayType: string
}

interface OuterProps {
  wallets: WalletDto[]
  tradingAccounts: TradingAccount[]
  availableTACurrencies: WalletCurrency[]
  fromAccount?: PresetAccount
  fetchRate(currencyFrom: CurrencyType, currencyTo: CurrencyType, options?: FetchRateOptions): void
  exchangeRate: number
  rateLoading: boolean
  limits: TransactionLimitDto[]

  onSubmit(values: FormSubmitValues<CreateConversionFormSubmitValues>): Promise<void>
}

export const ConversionForm = withFormik<OuterProps, CreateConversionFormValues>({
  mapPropsToValues: ({ fromAccount, limits, wallets }) => {
    const minAmount = 0.01
    const baseValues = {
      from: null,
      to: null,
      amountFrom: null,
      amountTo: null,
      minAmount,
      direction: ConversionDirection.From,
    }

    if (fromAccount?.account && fromAccount?.currency) {
      const { account, currency, type, displayType } = fromAccount
      const balances = account.platformOverview.balances
      const selectedBalance = balances.find((x) => x.currency === currency)
      const balance = selectedBalance?.balance || 0
      const walletTypeId =
        wallets.find((wallet) => wallet.id === account.wallet?.id)?.walletType.id ??
        WalletTypeEnum.ETD
      return {
        ...baseValues,
        from: {
          id: account.id,
          name: account.name,
          currency,
          balance,
          type,
          displayType,
          walletTypeId,
        },
      }
    }

    if (fromAccount?.wallet) {
      const minAmountLimit = limits.find(
        (x) =>
          x.walletType?.id === WalletTypeEnum.ETD &&
          x.currency.id === fromAccount.wallet?.currency.id
      )
      return {
        ...baseValues,
        minAmount: minAmountLimit?.amount ?? baseValues.minAmount,
        from: {
          id: fromAccount.wallet.id,
          name: fromAccount.wallet.name,
          currency: fromAccount.wallet.currency.id,
          balance: fromAccount.wallet.balance || 0,
          type: fromAccount.type,
          displayType: fromAccount.displayType,
          walletTypeId: fromAccount.wallet.walletType.id,
        },
      }
    }

    return baseValues
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<CreateConversionFormValues> = {}
    if (!values.from) {
      errors.from = t('Validation.Required')
    }
    if (!values.to) {
      errors.to = t('Validation.Required')
    }
    if (!values.amountFrom) {
      errors.amountFrom = t('Validation.Required')
    }
    if (!values.amountTo) {
      errors.amountTo = t('Validation.Required')
    }

    if (values.amountFrom && values.amountFrom < values?.minAmount) {
      errors.amountFrom = t('Validation.Minimum amount is {{amount}} {{currency}}', {
        currency: values?.from?.currency,
        amount: values?.minAmount,
      })
    }
    if (values.amountFrom && values.amountFrom > 0) {
      if (values.amountTo && values.amountTo < 0.01) {
        errors.amountTo = t('Validation.Minimum amount is {{amount}} {{currency}}', {
          currency: values?.to?.currency,
          amount: 0.01,
        })
      }
    }
    if (
      values?.amountFrom &&
      values.from?.balance != null &&
      values.amountFrom > values.from.balance
    ) {
      if (values.from.type === ConversionType.Wallet) {
        errors.amountFrom = t('Validation.There is not enough balance in your wallet')
      } else {
        errors.amountFrom = t('Validation.There is not enough balance in your trading account')
      }
    }
    return errors
  },
  enableReinitialize: true,
  validateOnMount: true,
})(ConversionFormUI)
